import * as React from 'react';
import { Link2, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Offer } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ooh/ui/components/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ooh/ui/components/tooltip';
import { CampaignNumberEditDialog, CampaignNumberMenuItem } from './campaign-number-edit-dialog';
import { CancelCampaignDialog, CancelCampaignMenuItem } from './cancel-campaign-dialog';
import { OfferAdminStatusDialog, OfferAdminStatusMenuItems } from './offer-admin-status-menu';

interface HeaderActionsMenuProps {
  canAdminChangeStatus: boolean;
  canEdit: boolean;
  canManageSharing: boolean;
  canTrash: boolean;
  isReadOnly: boolean;
  offer: Offer;
  onEdit: () => void;
  onManageSharing: () => void;
  onMoveToTrash: () => void;
  onClientShare: () => void;
}

type HeaderActionItemsProps = Pick<
  HeaderActionsMenuProps,
  | 'canEdit'
  | 'canManageSharing'
  | 'canTrash'
  | 'isReadOnly'
  | 'onEdit'
  | 'onManageSharing'
  | 'onMoveToTrash'
>;

function HeaderActionItems({
  canEdit,
  canManageSharing,
  canTrash,
  isReadOnly,
  onEdit,
  onManageSharing,
  onMoveToTrash,
}: HeaderActionItemsProps) {
  const { t } = useTranslation('offers');

  return (
    <>
      {!isReadOnly && canEdit ? (
        <DropdownMenuItem onSelect={onEdit}>{t('workspace.header.menu.edit')}</DropdownMenuItem>
      ) : null}
      {!isReadOnly && canManageSharing ? (
        <DropdownMenuItem onSelect={onManageSharing}>
          {t('workspace.header.menu.manageSharing')}
        </DropdownMenuItem>
      ) : null}
      {!isReadOnly && canTrash ? (
        <DropdownMenuItem onSelect={onMoveToTrash}>
          {t('workspace.header.menu.moveToTrash')}
        </DropdownMenuItem>
      ) : null}
    </>
  );
}

/** Extracted so the Tooltip/DropdownMenuTrigger/Button composition's own nesting
 * resets inside its own return statement, keeping HeaderActionsMenu's JSX under
 * the react/jsx-max-depth lint cap. */
function ActionsMenuTrigger({
  isActionsDisabled,
  tooltip,
}: {
  isActionsDisabled: boolean;
  tooltip: string;
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* span wrapper required: disabled buttons don't fire pointer events */}
          <span tabIndex={isActionsDisabled ? 0 : undefined}>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={tooltip}
                data-testid="offer-header-actions-trigger"
                disabled={isActionsDisabled}
                size="icon-sm"
                type="button"
                variant="outline"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface LifecycleDialogsState {
  targetStatus: Offer['status'] | null;
  setTargetStatus: (status: Offer['status'] | null) => void;
  isCancelDialogOpen: boolean;
  setIsCancelDialogOpen: (open: boolean) => void;
  isCampaignNumberDialogOpen: boolean;
  setIsCampaignNumberDialogOpen: (open: boolean) => void;
}

/** Groups the header's three offer-lifecycle dialogs so HeaderActionsMenu's own
 * complexity stays under the lint cap as menu items grow. */
function HeaderLifecycleDialogs({ offer, state }: { offer: Offer; state: LifecycleDialogsState }) {
  const {
    targetStatus,
    setTargetStatus,
    isCancelDialogOpen,
    setIsCancelDialogOpen,
    isCampaignNumberDialogOpen,
    setIsCampaignNumberDialogOpen,
  } = state;

  return (
    <>
      {targetStatus ? (
        <OfferAdminStatusDialog
          offer={offer}
          targetStatus={targetStatus}
          onClose={() => setTargetStatus(null)}
        />
      ) : null}
      {isCancelDialogOpen ? (
        <CancelCampaignDialog offer={offer} onClose={() => setIsCancelDialogOpen(false)} />
      ) : null}
      {isCampaignNumberDialogOpen ? (
        <CampaignNumberEditDialog
          offer={offer}
          onClose={() => setIsCampaignNumberDialogOpen(false)}
        />
      ) : null}
    </>
  );
}

export function HeaderActionsMenu({
  canAdminChangeStatus,
  canEdit,
  canManageSharing,
  canTrash,
  isReadOnly,
  offer,
  onEdit,
  onManageSharing,
  onMoveToTrash,
  onClientShare,
}: HeaderActionsMenuProps) {
  const { t } = useTranslation('offers');
  const [targetStatus, setTargetStatus] = React.useState<Offer['status'] | null>(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = React.useState(false);
  const [isCampaignNumberDialogOpen, setIsCampaignNumberDialogOpen] = React.useState(false);
  const canEditUnlessReadOnly = !isReadOnly && canEdit;
  const isActionsDisabled = isReadOnly ? !canAdminChangeStatus : false;

  return (
    <>
      <DropdownMenu>
        <ActionsMenuTrigger
          isActionsDisabled={isActionsDisabled}
          tooltip={t('workspace.header.moreActions')}
        />
        <DropdownMenuContent
          align="end"
          className="max-h-[min(var(--radix-popper-available-height),28rem)] overflow-y-auto"
        >
          {canAdminChangeStatus ? (
            <>
              <OfferAdminStatusMenuItems offer={offer} onStatusSelect={setTargetStatus} />
              <DropdownMenuSeparator />
            </>
          ) : null}
          <DropdownMenuItem data-testid="client-share-menu-item" onSelect={onClientShare}>
            <Link2 className="h-3.5 w-3.5" />
            {t('workspace.header.menu.clientShare')}
          </DropdownMenuItem>
          {!isReadOnly && (canEdit || canManageSharing) ? <DropdownMenuSeparator /> : null}
          <HeaderActionItems
            canEdit={canEdit}
            canManageSharing={canManageSharing}
            canTrash={canTrash}
            isReadOnly={isReadOnly}
            onEdit={onEdit}
            onManageSharing={onManageSharing}
            onMoveToTrash={onMoveToTrash}
          />
          <CampaignNumberMenuItem
            campaignNumber={offer.campaignNumber}
            canEdit={canEditUnlessReadOnly}
            onSelect={() => setIsCampaignNumberDialogOpen(true)}
          />
          <CancelCampaignMenuItem
            canEdit={canEditUnlessReadOnly}
            isCancelled={offer.status === 'CANCELLED'}
            onSelect={() => setIsCancelDialogOpen(true)}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <HeaderLifecycleDialogs
        offer={offer}
        state={{
          isCampaignNumberDialogOpen,
          isCancelDialogOpen,
          setIsCampaignNumberDialogOpen,
          setIsCancelDialogOpen,
          setTargetStatus,
          targetStatus,
        }}
      />
    </>
  );
}
